import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faPaperPlane,
  faSpinner,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/shared/FormField';
import {
  emailTemplateLibraryService,
  type SendTestResult,
} from '@/services/email-template-library.service';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Send Test — Phase 12.5C.
 *
 * This sends a REAL email, so nothing happens on open: the dialog only ever
 * dispatches from an explicit click on a control that is disabled until the
 * recipient validates.
 *
 * The test renders the content currently being SENT, not the unsaved draft — the
 * server resolves it the same way a real send would, and reports which side it
 * came from (`resolvedFrom`). Saying so plainly avoids the trap of someone
 * testing a draft they have not published and concluding it went live.
 */
export function SendTestDialog({
  open,
  templateKey,
  templateName,
  gatewayName,
  onClose,
}: {
  open: boolean;
  templateKey: string;
  templateName: string;
  gatewayName: string | null;
  onClose: () => void;
}) {
  const [recipient, setRecipient] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<SendTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setRecipient('');
    setResult(null);
    setError(null);
    setSending(false);
  }, [open]);

  const valid = EMAIL_RE.test(recipient.trim());

  const handleSend = async () => {
    if (!valid) return;
    setSending(true);
    setError(null);
    try {
      setResult(await emailTemplateLibraryService.sendTest(templateKey, recipient.trim()));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The test email could not be sent.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Send test — ${templateName}`}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          This sends a real email using the version your workspace currently sends, with sample
          values in place of the variables.
        </p>

        <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs">
          <span className="text-muted-foreground">Delivered through: </span>
          <span className="font-medium text-foreground">
            {gatewayName ?? 'your workspace default gateway'}
          </span>
          {!gatewayName && (
            <p className="mt-1 text-muted-foreground">
              No gateway is pinned to this template, so your workspace default is used — falling
              back to your service provider&rsquo;s gateway if you have none.
            </p>
          )}
        </div>

        <FormField label="Recipient email" htmlFor="tt-test-recipient" required>
          <Input
            id="tt-test-recipient"
            type="email"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="you@example.com"
            disabled={sending}
            autoComplete="off"
          />
          {recipient.trim() && !valid && (
            <p className="mt-1.5 text-xs text-destructive">Enter a valid email address.</p>
          )}
        </FormField>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {result && (
          <div
            className={
              result.success
                ? 'flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm'
                : 'flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive'
            }
          >
            <FontAwesomeIcon
              icon={result.success ? faCircleCheck : faTriangleExclamation}
              className="mt-0.5 h-3.5 w-3.5 shrink-0"
            />
            <span>
              {result.message}
              <span className="mt-0.5 block text-xs text-muted-foreground">
                Rendered from{' '}
                {result.resolvedFrom === 'TENANT'
                  ? 'your workspace version'
                  : 'the platform version'}
                .
              </span>
            </span>
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" className="min-h-[44px] sm:min-h-0" onClick={onClose}>
            Close
          </Button>
          <Button
            className="min-h-[44px] sm:min-h-0"
            onClick={() => void handleSend()}
            disabled={!valid || sending}
          >
            {sending ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="mr-1.5 h-3 w-3 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPaperPlane} className="mr-1.5 h-3 w-3" />
                Send test email
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
